import { Wifi, WifiOff } from "lucide-react";
import { combinedHealth } from "@/hooks/useRtkMeeting";

const TONE = {
  good: { dot: "bg-emerald-400", text: "text-emerald-300", label: "Good" },
  fair: { dot: "bg-amber-400", text: "text-amber-300", label: "Fair" },
  poor: { dot: "bg-red-500 animate-pulse", text: "text-red-300", label: "Poor" },
};

/**
 * Connection health for one peer.
 *  entry     { score, kind, at } from useConnectionHealth (SDK call stats)
 *  flow      { fps, stalledMs } from VideoTile onFlow (frames really reaching the tile) — remote tiles only
 *  uplink    { height, fps, kbps, reason } from useUplinkStats — self only
 *  direction "in" (what we receive from them) | "out" (our upload)
 *  size      "dot" (console tiles) | "badge" (camera / guest pages)
 */
export default function ConnectionDot({ entry, flow, uplink, direction = "in", size = "dot", className = "", testId }) {
  const { level, detail } = combinedHealth(entry, flow, uplink);
  const tone = level ? TONE[level] : null;
  const what = direction === "out" ? "upload from this device" : "picture coming in from this source";
  const title = tone
    ? `Connection ${tone.label.toLowerCase()} (${detail}) — ${what}${level === "poor" ? ". Move closer to the Wi‑Fi, switch to LTE, or lower other traffic." : ""}`
    : "Measuring connection…";
  const attrs = { title, "data-testid": testId, "data-level": level || "unknown", "data-detail": detail || "" };

  if (size === "badge") {
    const sub = uplink?.height ? `${uplink.height}p${uplink.reason === "bandwidth" ? " · weak connection" : uplink.reason === "cpu" ? " · phone busy" : ""}` : null;
    return (
      <div className={`flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1 text-[11px] font-medium ${tone ? tone.text : "text-zinc-400"} ${className}`} {...attrs}>
        {level === "poor" ? <WifiOff className="h-3.5 w-3.5" /> : <Wifi className="h-3.5 w-3.5" />}
        {tone ? `Connection: ${tone.label}` : "Checking connection…"}{sub && <span className="text-zinc-400"> · {sub}</span>}
      </div>
    );
  }
  return <span className={`inline-flex h-2.5 w-2.5 shrink-0 rounded-full ${tone ? tone.dot : "bg-zinc-600"} ${className}`} {...attrs} />;
}
